/**
 * BookingEngineSignup.js
 * ----------------------------------------------------------------------------
 * Dedicated signup for the Booking Engine product. Separate from the general
 * vendor register so the whole flow is booking-only:
 *
 *   1. POST /api/auth/register  (role: vendor)  -> creates user + vendor row
 *   2. setAuthToken(token)      -> bearer now rides on the shared axios instance
 *   3. POST /api/booking-v2/vendor/enable-v2  -> flips the flag + seeds hours
 *   4. navigate to /booking-engine/onboarding  (NOT the loyalty setup wizard)
 *
 * Add the route in App.js:
 *   <Route path="/booking-engine/signup" element={<BookingEngineSignup />} />
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { setAuthToken } from '../utils/auth';

function BookingEngineSignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ businessName: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.businessName.trim()) return setError('Add your business name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setError('Enter a valid email.');
    if (form.password.length < 6) return setError('Use at least 6 characters for your password.');

    setLoading(true);
    try {
      // 1. Register as a vendor (creates the vendor row).
      const res = await axios.post('/api/auth/register', {
        email: form.email,
        password: form.password,
        name: form.businessName,
        role: 'vendor',
      });

      // 2. Attach the token to the shared axios instance.
      const token = res.data.token;
      localStorage.setItem('token', token);
      setAuthToken(token);

      // 3. Turn the booking engine on for this brand-new vendor.
      await axios.post('/api/booking-v2/vendor/enable-v2');

      // 4. Straight into booking-only onboarding.
      navigate('/booking-engine/onboarding');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create your account. Try again.');
      setLoading(false);
    }
  };

  return (
    <div className="bes">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&display=swap');
        .bes { --ink:#11202e; --mint:#2fb89a; --mint-deep:#1b6f57; --amber:#ffb454;
          --slate:#5c6b73; --line:#e3ebe9; --surface:#f5f8f7;
          min-height:100vh; background:var(--surface); color:var(--ink);
          font-family:'Inter',system-ui,sans-serif; display:flex; align-items:center; justify-content:center; padding:32px 20px; }
        .bes * { box-sizing:border-box; }
        .bes-card { background:#fff; border:1px solid var(--line); border-radius:18px;
          padding:36px 32px; width:100%; max-width:420px; box-shadow:0 24px 50px -30px rgba(17,32,46,.4); }
        .bes-eyebrow { font-family:'Space Grotesk',sans-serif; font-size:12px; font-weight:500;
          letter-spacing:.16em; text-transform:uppercase; color:var(--mint-deep); }
        .bes h1 { font-family:'Space Grotesk',sans-serif; font-weight:700; letter-spacing:-.02em;
          font-size:26px; margin:8px 0 6px; }
        .bes .sub { color:var(--slate); font-size:14px; margin:0 0 24px; }
        .bes label { display:block; font-size:13px; font-weight:600; margin:14px 0 6px; }
        .bes input { width:100%; padding:12px 13px; border:1px solid var(--line); border-radius:10px;
          font-size:15px; font-family:inherit; }
        .bes input:focus { outline:3px solid rgba(47,184,154,.35); outline-offset:1px; border-color:var(--mint); }
        .bes .err { background:#fdecec; color:#b23b3b; padding:10px 12px; border-radius:9px;
          font-size:13px; margin-top:16px; }
        .bes button { margin-top:22px; width:100%; padding:13px; border:none; border-radius:11px;
          background:var(--ink); color:#fff; font-weight:600; font-size:15px; cursor:pointer; }
        .bes button:disabled { opacity:.6; cursor:default; }
        .bes .foot { text-align:center; font-size:13px; color:var(--slate); margin-top:18px; }
        .bes .foot a { color:var(--ink); font-weight:600; }
      `}</style>

      <div className="bes-card">
        <div className="bes-eyebrow">Booking engine</div>
        <h1>Start taking bookings</h1>
        <p className="sub">Create your account and your booking page goes live in a few minutes.</p>

        {/* No <form> submit reload issues: button drives handleSubmit */}
        <div>
          <label htmlFor="bes-name">Business name</label>
          <input id="bes-name" type="text" value={form.businessName}
            onChange={update('businessName')} placeholder="e.g. Lumi Hair Studio" />

          <label htmlFor="bes-email">Work email</label>
          <input id="bes-email" type="email" value={form.email}
            onChange={update('email')} placeholder="you@business.com" />

          <label htmlFor="bes-pass">Password</label>
          <input id="bes-pass" type="password" value={form.password}
            onChange={update('password')} placeholder="At least 6 characters"
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)} />

          {error && <div className="err">{error}</div>}

          <button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Setting things up…' : 'Create account & continue'}
          </button>

          <p className="foot">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default BookingEngineSignup;